import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $usersStore } from '@lib/store';
import { cn } from '@shared/utils';
import { UserType, type AccountResponse, type CardResponse, type UserWithPaymentInfo } from '@shared/api';
import { SmallAccount } from './smallAccount';
import { SmallCard } from './smallCard';
import { AddAccountsModal, AddCardsModal } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import AccountIcon from '@assets/icons/account.svg';
import CardIcon from '@assets/icons/card.svg';
import PlusIcon from '@assets/icons/plus.svg';

interface UserLongInfoItemProps {
  className?: string;
  show: boolean;
  user: UserWithPaymentInfo;
  cards?: CardResponse[];
  accounts?: AccountResponse[];
}

export const UserLongInfoItem = (props: UserLongInfoItemProps) => {
  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showAddCards, setShowAddCards] = useState<boolean>(false);

  const { t } = useTranslation('userManagement');

  const removeAccountHandler = (id: number) => $usersStore.events.removeAccount({ userIds: props.user.id, accountIds: [id] });
  const removeCardHandler = (id: number) => $usersStore.events.removeCard({ userIds: props.user.id, cardIds: [id] });
  return (
    <div className={cn('border-t border-t-gray-black px-6 pb-8 pt-5', props.show ? 'block' : 'hidden', props.className)}>
      <div className='mb-6 flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2'>
            <Icon
              className='flex h-8 w-8 flex-none items-center justify-center bg-ice dark:bg-eerie-black'
              color='secondary-inverse'
              icon={<AccountIcon />}
            />
            <Text weight='medium' size='sm'>
              {t('user.info.userType.manager', { count: props.accounts?.length })}
            </Text>
          </div>
          {props.user.userType === UserType.User && (
            <div className='flex items-center gap-2'>
              <Icon
                className='flex h-8 w-8 flex-none items-center justify-center bg-ice dark:bg-eerie-black'
                color='secondary-inverse'
                icon={<CardIcon />}
              />
              <Text weight='medium' size='sm'>
                {t('user.info.userType.user', { count: props.cards?.length })}
              </Text>
            </div>
          )}
        </div>
        <Button
          className='flex h-8 w-8 flex-none items-center justify-center rounded p-0 text-center'
          color='outlined'
          onClick={props.user.userType === UserType.Manager ? () => setShowAddAccounts(true) : () => setShowAddCards(true)}
        >
          <Icon icon={<PlusIcon />} color='inherit' />
        </Button>
      </div>
      <div className='flex flex-col gap-6'>
        {props.accounts && props.accounts.map((account) => <SmallAccount key={account.id} account={account} onRemove={removeAccountHandler} />)}
        {props.cards && props.cards.map((card) => <SmallCard key={card.cardId} card={card} onRemove={removeCardHandler} />)}
      </div>
      <AddAccountsModal
        isOpen={showAddAccounts}
        user={props.user}
        accountsItems={props.user.accounts}
        onClose={() => setShowAddAccounts(false)}
        onSubmit={(data) => $usersStore.events.addAccount(data)}
      />
      <AddCardsModal
        isOpen={showAddCards}
        user={props.user}
        accountsItems={props.user.accounts}
        cardsItems={props.user.cards}
        onClose={() => setShowAddCards(false)}
        onSubmit={(data) =>
          Promise.all([
            data.accountIds.length > 0 && $usersStore.events.addAccount(data),
            data.cardIds.length > 0 && $usersStore.events.addCard(data),
          ])
        }
      />
    </div>
  );
};
